import type { EliosEnv } from "@/config/env";
import type { OperatorClaims } from "./claims";

/**
 * UX-only capability gating (§4.4 / §9.2 of the plan).
 *
 * The backend is the real authority — these flags only decide what the SPA
 * offers/enables. The backend ships `admin` (baseline) + `superAdmin` (a strict
 * elevation above it). The split, by environment:
 *
 *   - dev/staging — `admin` does everything.
 *   - production  — `admin` does non-destructive actions; DESTRUCTIVE ones
 *                   (revoke / terminate / decline / reverse / expire / reset)
 *                   additionally require `superAdmin`.
 *
 * `superAdmin` also gates the collateral-withdrawal treasury surface in every
 * environment. The model stays extensible: when the backend adds more roles
 * (viewer / operator), narrow the predicates here and the UI follows with no
 * screen changes.
 */
export type Capability =
  | "read" // browse every collection
  | "act" // non-destructive admin actions (e.g. install/request a session key)
  | "act.destructive" // revoke / terminate / decline / reverse / expire
  | "act.prod"; // act at all against the production environment

const FUTURE_ROLES = {
  viewer: "viewer",
  operator: "operator",
} as const;

/**
 * The real backend `superAdmin` role (`@elios-backend/common` `UserRole.SuperAdmin`) — a strict
 * elevation above `admin`. Gates the collateral-withdrawal treasury surface in all environments,
 * and destructive actions against production.
 */
const SUPER_ADMIN_ROLE = "superAdmin";

export interface Capabilities {
  read: boolean;
  act: boolean;
  actDestructive: boolean;
  actProd: boolean;
  /** The operator holds the `superAdmin` role — required for treasury actions (e.g. collateral withdrawal). */
  superAdmin: boolean;
}

export function capabilitiesFor(claims: OperatorClaims, eliosEnv: EliosEnv): Capabilities {
  const isAdmin = claims.isAdmin && claims.backendId.length > 0;
  // A future explicit `viewer` role means read-only — no acting even with `admin`.
  const isViewerOnly = claims.roles.includes(FUTURE_ROLES.viewer) && !claims.isAdmin;
  const isSuperAdmin = claims.roles.includes(SUPER_ADMIN_ROLE);
  const isProd = eliosEnv === "production";

  const canAct = isAdmin && !isViewerOnly;
  return {
    read: isAdmin,
    act: canAct,
    // Outside production any admin may act destructively. In production the blast radius is real,
    // so destructive actions are held to `superAdmin` — the same elevation the treasury uses.
    actDestructive: isProd ? canAct && isSuperAdmin : canAct,
    // Acting in production at all requires no extra role beyond `admin`; the destructive subset is
    // narrowed by `actDestructive` above.
    actProd: canAct,
    superAdmin: isSuperAdmin,
  };
}

export function can(caps: Capabilities, capability: Capability): boolean {
  switch (capability) {
    case "read":
      return caps.read;
    case "act":
      return caps.act;
    case "act.destructive":
      return caps.actDestructive;
    case "act.prod":
      return caps.actProd;
  }
}
